/**
 * Customer-facing copy for the one guest-checkout failure that no retry can
 * fix: the email belongs to an account in this org.
 *
 * Kept out of `stripe-client` on purpose — every component test mocks that
 * module wholesale for its network calls, which would leave these functions
 * undefined and blow up the very error path they explain.
 *
 * The detector that decides when to use them is
 * {@link import('../payments/stripe-client').isAccountExistsError}, next to the
 * API's `GUEST_CHECKOUT_ACCOUNT_EXISTS_CODE`.
 */

/**
 * The one wording every embed uses for {@link isAccountExistsError}, so the
 * customer meets the same explanation wherever the recognised email turns up.
 *
 * Two things have to be said, and the old copy said neither plainly: that we
 * RECOGNISE this specific address (the customer typed it several steps back,
 * and an unattributed "an account already exists" reads as a system fault),
 * and that a guest order with it is impossible rather than merely failing —
 * signing in is the only way through, so retrying is pointless.
 *
 * @param email the recognised address; named in the message when known
 * @param action what they were doing — "check out", "book", "register"
 */
export function accountExistsMessage(email?: string, action = 'check out') {
  const address = email?.trim();
  return `${
    address ? `We recognize ${address} — that email` : 'That email'
  } already has an account here, so you can't ${action} as a guest with it. Sign in to continue.`;
}

/**
 * Same recognition, but the address belongs to someone the buyer named — a
 * co-attendee, an extra ticket holder, a gift recipient. Sign-in is NOT the
 * remedy here: the person in front of us isn't the account holder, so offering
 * to sign them in would hand them someone else's checkout. Give them the two
 * things they can actually do instead.
 */
export function accountExistsForOtherMessage(email?: string, name?: string) {
  const address = email?.trim();
  const who = name?.trim() || 'They';
  return `${
    address ? `We recognize ${address}. ` : ''
  }${who} already ${who === 'They' ? 'have' : 'has'} an account here, so they can't be added as a guest. Use a different email address, or ask them to sign in and do it themselves.`;
}
